/* eslint-disable react/no-unescaped-entities */
/**
 * NegociationDossier.jsx — Espace privé du dossier (créateur authentifié).
 *
 * Affiche la chronologie, permet d'ajouter propositions, messages, pièces,
 * de changer le statut, et de télécharger la « Synthèse des conditions
 * déclarées par les parties » (PDF).
 *
 * Charte stricte (`/app/memory/NEGOCIATION_CHARTER.md`).
 */
import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import OrientationPanel from "@/components/OrientationPanel";
import { Button } from "@/components/ui/button";
import { apiClient, formatApiError } from "@/lib/api";
import { emitExpertLead } from "@/lib/negociationTracking";
import { toast } from "sonner";
import {
  ArrowLeft, Send, Paperclip, FileDown, Loader2, ShieldCheck, Clock,
  RefreshCw, Mail, Copy, FileText, MessageSquare, Upload, Sparkles,
} from "lucide-react";

const COMPLIANCE = (
  "Confilex est un outil de structuration documentaire et de formalisation " +
  "d'informations fournies par les utilisateurs. Confilex ne fournit aucune " +
  "consultation juridique, n'analyse pas les droits des parties, ne vérifie " +
  "pas la validité des créances et ne rédige aucun acte juridique."
);

const STATUS_OPTIONS = [
  { key: "open",                label: "🟢 Discussion ouverte" },
  { key: "documents_provided",  label: "🟢 Documents transmis" },
  { key: "counter_received",    label: "🟡 Contre-proposition reçue" },
  { key: "in_discussion",       label: "🟡 Conditions en cours de discussion" },
  { key: "converging",          label: "🟢 Conditions rapprochées" },
  { key: "synthese_exported",   label: "🎉 Synthèse finale exportée" },
  { key: "interrupted",         label: "🔴 Discussions interrompues" },
];

export default function NegociationDossier() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [orientation, setOrientation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msgBody, setMsgBody] = useState("");
  const [proposal, setProposal] = useState({ amount_eur: "", schedule: "", declared_conditions: "" });
  const fileRef = useRef(null);

  const load = async () => {
    try {
      const [d, o] = await Promise.all([
        apiClient.get(`/negotiation/cases/${id}`),
        apiClient.get(`/negotiation/cases/${id}/orientation`).catch(() => null),
      ]);
      setData(d.data);
      if (o) setOrientation(o.data);
      setError(null);
    } catch (e) {
      setError(formatApiError(e?.response?.data?.detail) || "Dossier introuvable");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const sendMessage = async () => {
    if (!msgBody.trim()) return;
    setBusy(true);
    try {
      await apiClient.post(`/negotiation/cases/${id}/messages`, { body: msgBody.trim() });
      setMsgBody("");
      await load();
    } catch (e) {
      toast.error("Envoi impossible", { description: formatApiError(e?.response?.data?.detail) });
    } finally { setBusy(false); }
  };

  const sendProposal = async () => {
    if (!proposal.declared_conditions.trim() || proposal.declared_conditions.trim().length < 4) {
      toast.error("Conditions déclarées trop courtes");
      return;
    }
    setBusy(true);
    try {
      await apiClient.post(`/negotiation/cases/${id}/proposals`, {
        amount_eur: proposal.amount_eur === "" ? null : Number(proposal.amount_eur),
        schedule: proposal.schedule || null,
        declared_conditions: proposal.declared_conditions.trim(),
      });
      setProposal({ amount_eur: "", schedule: "", declared_conditions: "" });
      toast.success("Proposition formalisée");
      await load();
    } catch (e) {
      toast.error("Envoi impossible", { description: formatApiError(e?.response?.data?.detail) });
    } finally { setBusy(false); }
  };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      await apiClient.post(`/negotiation/cases/${id}/attachments`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Pièce déposée");
      await load();
    } catch (err) {
      toast.error("Upload impossible", { description: formatApiError(err?.response?.data?.detail) });
    } finally { setBusy(false); }
  };

  const changeStatus = async (s) => {
    setBusy(true);
    try {
      await apiClient.post(`/negotiation/cases/${id}/status`, { status: s });
      await load();
    } catch (e) {
      toast.error("Changement impossible", { description: formatApiError(e?.response?.data?.detail) });
    } finally { setBusy(false); }
  };

  const downloadPdf = async () => {
    setBusy(true);
    try {
      const r = await apiClient.get(`/negotiation/cases/${id}/synthese.pdf`, { responseType: "blob" });
      const blob = new Blob([r.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `synthese-conditions-declarees-${id.slice(0, 8)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Synthèse PDF générée");
      await load();
    } catch (e) {
      toast.error("Export impossible", { description: formatApiError(e?.response?.data?.detail) });
    } finally { setBusy(false); }
  };

  const copyInvite = () => {
    if (!data?.case?.invitation_url) return;
    const fullUrl = window.location.origin + data.case.invitation_url;
    navigator.clipboard.writeText(fullUrl);
    toast.success("Lien d'invitation copié", { description: fullUrl });
  };

  const transmitToExpert = async (profile, pertinence = null) => {
    setBusy(true);
    try {
      const { data: pkg } = await apiClient.post(`/negotiation/cases/${id}/transmission`);
      // Stocke le package en sessionStorage pour pré-remplir la prise de contact
      try {
        sessionStorage.setItem(`confilex-transmission-${id}`, JSON.stringify(pkg));
      } catch { /* quota */ }
      toast.success("Dossier prêt à transmettre", {
        description: "Vos échanges sont synthétisés. Direction le réseau de spécialistes.",
      });
      const route = {
        avocat: "/trouver-un-avocat",
        commissaire_de_justice: "/trouver-un-commissaire-de-justice",
        expert_comptable: "/trouver-un-expert-comptable",
        notaire: "/trouver-un-notaire",
        mediateur: "/trouver-un-mediateur",
      }[profile] || "/orientation-intelligente";
      // Redirection avec UTM complets + tracking
      const target = emitExpertLead({
        route, caseId: id, profile,
        level: orientation?.level, pertinence,
        action: "transmit",
      });
      window.location.href = target;
    } catch (e) {
      toast.error("Transmission impossible", { description: formatApiError(e?.response?.data?.detail) });
    } finally { setBusy(false); }
  };

  if (loading) return <FullPageLoader />;
  if (error) return <ErrorState message={error} />;

  const { case: c, proposals, messages, attachments, events } = data;
  // Construire timeline (fusion proposals + messages + events utiles)
  const timeline = [
    ...proposals.map(p => ({ kind: "proposal", at: p.created_at, payload: p })),
    ...messages.map(m => ({ kind: "message",  at: m.created_at, payload: m })),
    ...events
      .filter(e => ["attachment_uploaded", "status_changed", "synthesis_exported", "case_created"].includes(e.type))
      .map(e => ({ kind: "event", at: e.created_at, payload: e })),
  ].sort((a, b) => new Date(a.at) - new Date(b.at));

  return (
    <div className="min-h-screen bg-[#FAFAF7]">
      <Navbar variant="light" />
      <main className="max-w-6xl mx-auto px-6 lg:px-10 py-10" data-testid="negociation-dossier">
        <Link to="/negociation" className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-[#0B3A2A] mb-4">
          <ArrowLeft className="w-3.5 h-3.5" /> Tous mes dossiers
        </Link>

        {/* HEADER */}
        <div className="flex items-start justify-between gap-6 flex-wrap mb-6">
          <div>
            <p className="text-[11px] uppercase tracking-[0.3em] text-[#C9A961] font-medium mb-2">
              Dossier · {c.id.slice(0, 8)}
            </p>
            <h1 className="font-serif text-3xl text-[#0B3A2A]" data-testid="dossier-subject">{c.subject}</h1>
            <p className="mt-2 text-sm text-slate-600">
              Entre <b>{c.initiator_party.name}</b> et <b>{c.opposing_party.name || c.opposing_party.organization}</b>
              {c.amount_eur != null && <> · Montant déclaré : <b>{Number(c.amount_eur).toLocaleString("fr-FR", { style: "currency", currency: "EUR" })}</b></>}
            </p>
          </div>
          <div className="flex flex-col items-end gap-2">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-slate-200 text-sm" data-testid="status-badge">
              {c.status_icon} {c.status_label}
            </span>
            <select value={c.status} onChange={e => changeStatus(e.target.value)} disabled={busy}
              data-testid="status-select"
              className="text-xs px-2 py-1 rounded-lg border border-slate-200 bg-white">
              {STATUS_OPTIONS.map(o => <option key={o.key} value={o.key}>{o.label}</option>)}
            </select>
          </div>
        </div>

        {/* SCORE DE MATURITÉ + ORIENTATION INTELLIGENTE */}
        <OrientationPanel orientation={orientation} caseId={id} onTransmit={transmitToExpert} />

        {/* INVITATION + EXPORT */}
        <section className="rounded-2xl bg-white border border-slate-200 p-6 mb-6 grid md:grid-cols-2 gap-6">
          <div>
            <p className="text-[10px] uppercase tracking-wider text-[#C9A961] font-medium mb-2 flex items-center gap-1.5">
              <Mail className="w-3.5 h-3.5" /> Invitation de la partie destinataire
            </p>
            <p className="text-sm text-slate-600 mb-3">
              Partagez ce lien sécurisé. Il permet à la partie destinataire de consulter,
              répondre, contre-proposer et déposer des pièces sans créer de compte.
            </p>
            <div className="flex items-center gap-2">
              <input readOnly value={window.location.origin + c.invitation_url}
                data-testid="invitation-link"
                className="flex-1 text-xs px-3 py-2 rounded-lg border border-slate-200 bg-[#FAFAF7]" />
              <Button variant="outline" size="sm" onClick={copyInvite} data-testid="btn-copy-invite">
                <Copy className="w-3.5 h-3.5 mr-1" /> Copier
              </Button>
            </div>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wider text-[#C9A961] font-medium mb-2 flex items-center gap-1.5">
              <FileText className="w-3.5 h-3.5" /> Export
            </p>
            <p className="text-sm text-slate-600 mb-3">
              Téléchargez la « Synthèse des conditions déclarées par les parties » : horodatée,
              paginée, comportant la mention de conformité.
            </p>
            <Button onClick={downloadPdf} disabled={busy}
              className="bg-[#0B3A2A] hover:bg-[#0B3A2A] text-white" data-testid="btn-download-pdf">
              {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <FileDown className="w-4 h-4 mr-1.5" />}
              Télécharger la synthèse PDF
            </Button>
          </div>
        </section>

        {/* MAIN GRID */}
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Timeline */}
          <div className="lg:col-span-2 rounded-2xl bg-white border border-slate-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-serif text-lg text-[#0B3A2A]">Chronologie horodatée</h2>
              <Button variant="outline" size="sm" onClick={load} disabled={busy}>
                <RefreshCw className="w-3.5 h-3.5 mr-1" /> Rafraîchir
              </Button>
            </div>
            {timeline.length === 0 ? (
              <p className="text-sm text-slate-500 italic">Aucun échange pour le moment. Démarrez en proposant des conditions ou en envoyant un message.</p>
            ) : (
              <ul className="space-y-4" data-testid="timeline">
                {timeline.map((t, idx) => (
                  <TimelineItem key={idx} item={t} attachments={attachments} caseId={id} />
                ))}
              </ul>
            )}
          </div>

          {/* Actions panel */}
          <div className="space-y-6">
            {/* Proposition */}
            <div className="rounded-2xl bg-white border border-slate-200 p-5" data-testid="panel-proposal">
              <p className="text-[10px] uppercase tracking-wider text-[#C9A961] font-medium mb-3 flex items-center gap-1.5">
                <Sparkles className="w-3.5 h-3.5" /> Nouvelle proposition de règlement
              </p>
              <input type="number" min="0" step="0.01" placeholder="Montant (€)"
                value={proposal.amount_eur}
                onChange={e => setProposal(p => ({ ...p, amount_eur: e.target.value }))}
                data-testid="proposal-amount"
                className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm mb-2" />
              <input placeholder="Échéancier proposé (libre)"
                value={proposal.schedule}
                onChange={e => setProposal(p => ({ ...p, schedule: e.target.value }))}
                data-testid="proposal-schedule"
                maxLength={600}
                className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm mb-2" />
              <textarea placeholder="Conditions déclarées (texte libre)"
                value={proposal.declared_conditions}
                onChange={e => setProposal(p => ({ ...p, declared_conditions: e.target.value }))}
                data-testid="proposal-conditions"
                rows={4} maxLength={4000}
                className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm mb-2 resize-y" />
              <Button onClick={sendProposal} disabled={busy} size="sm"
                className="w-full bg-[#0B3A2A] hover:bg-[#0B3A2A] text-white" data-testid="btn-send-proposal">
                {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Send className="w-4 h-4 mr-1.5" />}
                Formaliser la proposition
              </Button>
            </div>

            {/* Message */}
            <div className="rounded-2xl bg-white border border-slate-200 p-5" data-testid="panel-message">
              <p className="text-[10px] uppercase tracking-wider text-[#C9A961] font-medium mb-3 flex items-center gap-1.5">
                <MessageSquare className="w-3.5 h-3.5" /> Message
              </p>
              <textarea value={msgBody} onChange={e => setMsgBody(e.target.value)}
                data-testid="message-body" rows={4} maxLength={4000}
                placeholder="Saisissez votre message à destination de l'autre partie."
                className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm mb-2 resize-y" />
              <Button onClick={sendMessage} disabled={busy || !msgBody.trim()} size="sm"
                className="w-full bg-[#0B3A2A] hover:bg-[#0B3A2A] text-white" data-testid="btn-send-message">
                {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Send className="w-4 h-4 mr-1.5" />}
                Envoyer
              </Button>
            </div>

            {/* Pièces */}
            <div className="rounded-2xl bg-white border border-slate-200 p-5" data-testid="panel-attachment">
              <p className="text-[10px] uppercase tracking-wider text-[#C9A961] font-medium mb-3 flex items-center gap-1.5">
                <Paperclip className="w-3.5 h-3.5" /> Pièces déposées
              </p>
              <ul className="space-y-2 text-sm mb-3">
                {attachments.length === 0 && <li className="text-slate-400 italic">Aucune pièce</li>}
                {attachments.map(a => (
                  <li key={a.id} className="flex items-center justify-between gap-2 text-xs">
                    <a href={`#`} onClick={(e) => { e.preventDefault(); downloadAttachment(id, a); }}
                      className="text-[#0B3A2A] hover:text-[#C9A961] truncate flex-1" title={a.filename}>
                      {a.filename}
                    </a>
                    <span className="text-slate-400">{(a.size / 1024).toFixed(0)} Ko</span>
                  </li>
                ))}
              </ul>
              <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} disabled={busy}
                className="w-full" data-testid="btn-attach">
                <Upload className="w-4 h-4 mr-1.5" /> Déposer une pièce
              </Button>
              <input type="file" hidden ref={fileRef} onChange={onFile} data-testid="input-attach"
                accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx,.xls,.xlsx,.txt,.csv" />
            </div>
          </div>
        </div>

        {/* Compliance */}
        <div className="mt-8 p-4 rounded-lg bg-[#FAF7F1] border border-[#C9A961]/30 text-xs text-[#0B3A2A] flex items-start gap-2" data-testid="compliance-notice">
          <ShieldCheck className="w-4 h-4 text-[#C9A961] mt-0.5 shrink-0" />
          <p><b>Mention de conformité :</b> {COMPLIANCE}</p>
        </div>
      </main>
      <Footer />
    </div>
  );
}

async function downloadAttachment(caseId, att) {
  try {
    const r = await apiClient.get(`/negotiation/cases/${caseId}/attachments/${att.id}`, { responseType: "blob" });
    const blob = new Blob([r.data], { type: att.content_type || "application/octet-stream" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = att.filename;
    a.click();
    URL.revokeObjectURL(url);
  } catch {
    toast.error("Téléchargement impossible");
  }
}

function TimelineItem({ item, attachments, caseId }) {
  const dt = item.at?.slice(0, 19).replace("T", " ");
  const kindBadge = (() => {
    if (item.kind === "proposal") return { c: "bg-[#C9A961]/20 text-[#0B3A2A] border-[#C9A961]/40", label: "Proposition" };
    if (item.kind === "message")  return { c: "bg-[#0B3A2A]/10 text-[#0B3A2A] border-[#0B3A2A]/20", label: "Message" };
    return { c: "bg-slate-100 text-slate-700 border-slate-200", label: "Évènement" };
  })();

  return (
    <li className="border border-slate-200 rounded-lg p-4 bg-white">
      <div className="flex items-center gap-2 text-[11px] text-slate-500 mb-2">
        <span className={`px-2 py-0.5 rounded-full border ${kindBadge.c}`}>{kindBadge.label}</span>
        <Clock className="w-3 h-3" /> {dt}
        <span className="ml-auto text-slate-500">
          {item.payload?.author?.display_name ||
            (item.kind === "event" ? item.payload?.actor?.display_name : "—")}
          {item.payload?.author?.kind && <span className="ml-1 text-slate-400">({item.payload.author.kind})</span>}
        </span>
      </div>

      {item.kind === "proposal" && (
        <div className="text-sm text-[#0B3A2A]">
          <p className="font-medium mb-1">Version {item.payload.version}</p>
          {item.payload.amount_eur != null && (
            <p className="text-xs text-slate-600">
              Montant proposé : <b>{Number(item.payload.amount_eur).toLocaleString("fr-FR", { style: "currency", currency: "EUR" })}</b>
            </p>
          )}
          {item.payload.schedule && <p className="text-xs text-slate-600">Échéancier : {item.payload.schedule}</p>}
          <p className="text-sm mt-2 whitespace-pre-wrap">{item.payload.declared_conditions}</p>
        </div>
      )}

      {item.kind === "message" && (
        <p className="text-sm text-[#0B3A2A] whitespace-pre-wrap">{item.payload.body}</p>
      )}

      {item.kind === "event" && (
        <p className="text-sm text-slate-600">
          {item.payload.type === "case_created" && "Dossier créé."}
          {item.payload.type === "status_changed" && `Statut changé : ${item.payload.payload?.label || item.payload.payload?.new_status}`}
          {item.payload.type === "attachment_uploaded" && (
            <>Pièce déposée : <b>{item.payload.payload?.filename}</b> ({(item.payload.payload?.size / 1024).toFixed(0)} Ko)</>
          )}
          {item.payload.type === "synthesis_exported" && "Synthèse PDF générée et téléchargée."}
        </p>
      )}
    </li>
  );
}

function FullPageLoader() {
  return (
    <div className="min-h-screen bg-[#FAFAF7] flex items-center justify-center">
      <Loader2 className="w-8 h-8 text-[#C9A961] animate-spin" />
    </div>
  );
}

function ErrorState({ message }) {
  return (
    <div className="min-h-screen bg-[#FAFAF7]">
      <Navbar variant="light" />
      <main className="max-w-md mx-auto px-6 py-20 text-center">
        <h1 className="font-serif text-2xl text-[#0B3A2A] mb-3">Dossier introuvable</h1>
        <p className="text-sm text-slate-600 mb-6">{message}</p>
        <Button asChild><Link to="/negociation">Retour</Link></Button>
      </main>
      <Footer />
    </div>
  );
}
